// TipTap editor wrapper — Word-like WYSIWYG for Maria Gioia (Italian UI).
// Never exposes HTML/Markdown/JSON. Toolbar with bold/italic/heading/list/
// link/image. Images are uploaded to /admin/articles/media and inserted as
// TipTap image nodes referencing the returned media_id.

import { useCallback, useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import api from "@/modules/growing-circle/lib/api";
import { toast } from "sonner";
import { Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3, List, ListOrdered, Link2, Image as ImgIcon, Undo, Redo } from "lucide-react";

const CustomImage = Image.extend({
  addAttributes() {
    return {
      media_id: { default: null },
      alt: { default: "" },
      decorative: { default: false },
      caption: { default: "" },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes.media_id
      ? `${process.env.REACT_APP_BACKEND_URL}/api/growing-circle/article-media/${HTMLAttributes.media_id}`
      : HTMLAttributes.src;
    return ["img", { src, alt: HTMLAttributes.decorative ? "" : (HTMLAttributes.alt || ""), style: "max-width:100%" }];
  },
});

function ToolbarBtn({ onClick, active, disabled, label, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testId}
      className={`p-2 rounded transition-colors ${active ? "bg-primary/20 text-primary" : "text-foreground/70 hover:bg-white/5 hover:text-foreground"} disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

export default function TipTapEditor({ value, onChange, editable = true, compact = false, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      CustomImage,
      Placeholder.configure({ placeholder: placeholder || "Inizia a scrivere il tuo articolo…" }),
    ],
    content: value || { type: "doc", content: [{ type: "paragraph" }] },
    editable,
    onUpdate: ({ editor }) => onChange && onChange(editor.getJSON()),
  }, [editable]);

  // Sync external `value` prop into the editor when it changes AFTER mount.
  // The parent loads the article body via API (async) so on first render
  // `value` is the empty doc; when the real body arrives we must push it
  // into TipTap without re-emitting an onUpdate (which would clobber it).
  useEffect(() => {
    if (!editor || !value) return;
    try {
      const current = editor.getJSON();
      if (JSON.stringify(current) !== JSON.stringify(value)) {
        editor.commands.setContent(value, false);
      }
    } catch { /* editor not ready */ }
  }, [editor, value]);

  const insertLink = useCallback(() => {
    const url = window.prompt("URL del link (https://…):");
    if (!url) return;
    if (!/^https?:\/\//.test(url)) { toast.error("Inserisci un URL valido (https://…)"); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const uploadImage = useCallback(async (file) => {
    if (!file) return;
    const alt = window.prompt("Testo alternativo per l'immagine (lascia vuoto se decorativa):", "") || "";
    const decorative = !alt.trim();
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/articles/media", form, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().insertContent({
        type: "image",
        attrs: { media_id: data.media_id, alt, decorative },
      }).run();
    } catch (e) {
      toast.error("Caricamento immagine fallito.");
    }
  }, [editor]);

  if (!editor) return null;
  return (
    <div className="border border-border rounded-xl bg-card" data-testid="tiptap-editor">
      {/* Floating BubbleMenu — appears next to any selected text, so the
       * author can format from the middle/bottom of a long article without
       * scrolling back to the top toolbar. */}
      <BubbleMenu editor={editor}>
        <div className="flex items-center gap-0.5 rounded-lg border border-border bg-card px-1 py-1 shadow-lg">
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Grassetto" testId="bubble-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Corsivo" testId="bubble-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} label="Sottolineato" testId="bubble-underline"><UnderlineIcon className="w-4 h-4" /></ToolbarBtn>
          <span className="w-px h-5 bg-border mx-0.5" />
          <ToolbarBtn onClick={insertLink} active={editor.isActive("link")} label="Link" testId="bubble-link"><Link2 className="w-4 h-4" /></ToolbarBtn>
        </div>
      </BubbleMenu>
      {/* Main toolbar — sticky so it stays visible while scrolling the
       * article body. `top-0` sticks to the viewport top; the modal/page
       * scroll container is the SPA <main>. */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-2 py-2 sticky top-0 z-20 bg-card rounded-t-xl">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Grassetto" testId="ed-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Corsivo" testId="ed-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} label="Sottolineato" testId="ed-underline"><UnderlineIcon className="w-4 h-4" /></ToolbarBtn>
        {!compact && <>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Titolo" testId="ed-h2"><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Sottotitolo" testId="ed-h3"><Heading3 className="w-4 h-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Elenco puntato" testId="ed-ul"><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Elenco numerato" testId="ed-ol"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
        </>}
        <span className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn onClick={insertLink} active={editor.isActive("link")} label="Link" testId="ed-link"><Link2 className="w-4 h-4" /></ToolbarBtn>
        {!compact && (
          <label className="p-2 rounded text-foreground/70 hover:bg-white/5 hover:text-foreground cursor-pointer" title="Inserisci immagine" data-testid="ed-image">
            <ImgIcon className="w-4 h-4" />
            <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => uploadImage(e.target.files?.[0])} />
          </label>
        )}
        <span className="flex-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} label="Annulla" testId="ed-undo"><Undo className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} label="Ripeti" testId="ed-redo"><Redo className="w-4 h-4" /></ToolbarBtn>
      </div>
      <EditorContent editor={editor} className={`prose prose-invert max-w-none px-6 py-6 focus:outline-none ${compact ? "min-h-[120px]" : "min-h-[420px]"}`} />
    </div>
  );
}
